import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { Check } from 'lucide-react';
import bgImage from '../img/idcardback.png';       // ← from Adminprofile
// import logoImage from '../img/idlogo.png';          // ← from Adminprofile
import logoImage from '../img/spllogo.png';
import "./Profile.css";

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [mealDistribution, setMealDistribution] = useState([
    { id: 1, date: '2025-10-29', breakfast: true,  lunch: true,  hiTea: true,  dinner: false },
    { id: 2, date: '2025-10-30', breakfast: true,  lunch: false, hiTea: false, dinner: false },
    { id: 3, date: '2025-10-31', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 4, date: '2025-11-01', breakfast: false, lunch: false, hiTea: false, dinner: false },
    { id: 5, date: '2025-11-02', breakfast: false, lunch: false, hiTea: false, dinner: false },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrData, setQrData] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const idCardRef = useRef(null);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getUserPhotoUrl = (userData) => {
    if (!userData) return null;
    const photoFields = [
      'photoPath', 'PhotoPath', 'photo', 'Photo',
      'photoUrl', 'PhotoUrl', 'PhotoURL',
      'image', 'Image', 'imageUrl', 'ImageUrl', 'ImageURL',
      'profilePicture', 'ProfilePicture',
      'profilePhoto', 'ProfilePhoto',
      'avatar', 'Avatar', 'picture', 'Picture',
    ];
    for (const field of photoFields) {
      if (userData[field] && userData[field].toString().trim()) {
        const v = userData[field].toString().trim();
        if (v.startsWith('http://') || v.startsWith('https://')) return v;
        if (v.startsWith('data:image')) return v;
        if (v.startsWith('/')) return `https://api.asterickx.com${v}`;
        return `https://api.asterickx.com/uploads/${v}`;
      }
    }
    return null;
  };

  const getUserPhone = (userData) => {
    if (!userData) return null;
    const fields = ['phoneNumber', 'PhoneNumber', 'phone', 'Phone', 'mobile', 'Mobile', 'mobileNumber', 'MobileNumber', 'contactNumber', 'ContactNumber'];
    for (const f of fields) {
      if (userData[f] && userData[f].toString().trim()) return userData[f].toString().trim();
    }
    return null;
  };

  const getUserState = (userData) => {
    if (!userData) return null;
    const fields = ['state', 'State', 'stateName', 'StateName', 'location', 'Location', 'region', 'Region'];
    for (const f of fields) {
      if (userData[f] && userData[f].toString().trim()) return userData[f].toString().trim();
    }
    return null;
  };

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('JWT decode error:', e);
      return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return 'Invalid Date'; }
  };

  const formatDayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return 'Invalid Date'; }
  };

  // ─── Auth ────────────────────────────────────────────────────────────────────

  const checkAuthWithRetry = async (retryCount = 0, maxRetries = 3) => {
    const storedUser = localStorage.getItem('user');
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('Token') ||
      (storedUser ? JSON.parse(storedUser).token : null);

    if (!token && retryCount < maxRetries) {
      await new Promise(r => setTimeout(r, 200));
      return checkAuthWithRetry(retryCount + 1, maxRetries);
    }

    if (!token) {
      console.error('No token found after all retries');
      navigate('/login');
      return null;
    }

    try {
      const decodedToken = decodeJWT(token);
      if (!decodedToken) { navigate('/login'); return null; }

      const userFromToken = {
        id:
          decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
          decodedToken.sub ||
          decodedToken.userId,
        email:
          decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
          decodedToken.email,
        name:
          decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
          decodedToken.name,
        exp: decodedToken.exp,
      };

      return { userFromToken, token };
    } catch (e) {
      console.error('Auth error:', e);
      localStorage.clear();
      navigate('/login');
      return null;
    }
  };

  // ─── Download ID Card (from Adminprofile) ────────────────────────────────────

  const downloadIDCard = async () => {
    if (!idCardRef.current) { alert('ID card not found'); return; }

    try {
      setDownloading(true);

      const storedUser = localStorage.getItem('user');
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('Token') ||
        (storedUser ? JSON.parse(storedUser)?.token : null);

      const currentUserData = userDetails || userData;
      const userId =
        currentUserData?.id ||
        currentUserData?.userId ||
        currentUserData?.memberId ||
        currentUserData?.ID;

      // STEP 1: Fetch base64 photo to avoid CORS issues in html2canvas
      let imageBase64 = null;
      if (userId) {
        try {
          const res = await fetch(
            `https://api.asterickx.com/api/Members/members/photo/${userId}`,
            { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            if (data.base64) imageBase64 = data.base64;
          }
        } catch (e) {
          console.warn('Photo fetch failed, will try existing src', e);
        }
      }

      // STEP 2: Swap profile photo src to base64 on the live card
      const profileImg = idCardRef.current.querySelector('img[alt="Profile"]');
      let originalSrc = null;
      if (imageBase64 && profileImg) {
        originalSrc = profileImg.src;
        profileImg.src = imageBase64.startsWith('data:image')
          ? imageBase64
          : `data:image/jpeg;base64,${imageBase64}`;
        await new Promise(r => {
          if (profileImg.complete) { r(); return; }
          profileImg.onload = r;
          profileImg.onerror = r;
        });
      }

      // STEP 3: Hide download button temporarily
      const btnContainer = idCardRef.current.querySelector('[data-exclude-from-capture]');
      if (btnContainer) btnContainer.style.visibility = 'hidden';

      await new Promise(r => requestAnimationFrame(() => setTimeout(r, 100)));

      // STEP 4: Capture
      const card = idCardRef.current;
      const { width, height } = card.getBoundingClientRect();

      const canvas = await html2canvas(card, {
        scale: 4,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        imageTimeout: 20000,
        width: Math.round(width),
        height: Math.round(height),
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        onclone: (_doc, el) => {
          const btn = el.querySelector('[data-exclude-from-capture]');
          if (btn) btn.remove();
          el.querySelectorAll('img').forEach(img => {
            img.style.visibility = 'visible';
            img.style.opacity = '1';
            img.style.display = 'block';
          });
        },
      });

      // STEP 5: Restore
      if (originalSrc && profileImg) profileImg.src = originalSrc;
      if (btnContainer) btnContainer.style.visibility = 'visible';

      // STEP 6: Build PDF
      const cardWidthMm = 100;
      const cardHeightMm = Math.round(cardWidthMm * (height / width));

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [cardWidthMm, cardHeightMm],
        compress: true,
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, cardWidthMm, cardHeightMm);

      const displayUserData = userDetails || userData;
      const userName = (displayUserData?.firstName || displayUserData?.name || 'User')
        .replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`ID_Card_${userName}_${Date.now()}.pdf`);

    } catch (err) {
      console.error('Download error:', err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  // ─── QR ──────────────────────────────────────────────────────────────────────

  const generateQRData = async (userId) => {
    try {
      setQrLoading(true);
      const qrUrl = `${window.location.origin}/userqr?id=${userId}`;
      setQrData(qrUrl);
      return qrUrl;
    } catch (e) {
      const fallback = `${window.location.origin}/userqr?id=${userId}`;
      setQrData(fallback);
      return fallback;
    } finally {
      setQrLoading(false);
    }
  };

  // ─── Init ─────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const initializeProfile = async () => {
      const authResult = await checkAuthWithRetry();
      if (!authResult) return;

      const { userFromToken, token } = authResult;
      setUserData(userFromToken);

      if (!userFromToken.id) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      fetchUserDetails(userFromToken.id, token);
      generateQRData(userFromToken.id);
    };

    initializeProfile();
  }, [navigate]);

  // ─── Fetch user + tournaments (from Profile) ──────────────────────────────────

  const fetchUserDetails = async (userId, token) => {
    try {
      setLoading(true);
      setError(null);

      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      // User details
      const userResponse = await fetch(`https://api.asterickx.com/api/Members/${userId}`, {
        method: 'GET', headers, mode: 'cors',
      });

      if (userResponse.ok) {
        const userDetailsData = await userResponse.json();
        setUserDetails(userDetailsData);
      } else if (userResponse.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => { localStorage.clear(); navigate('/login'); }, 2000);
        return;
      } else {
        setError(`Failed: ${userResponse.status}`);
      }

      // Tournaments + teams membership
      try {
        const tournamentsResponse = await fetch('https://api.asterickx.com/api/Tournaments', { headers });

        if (!tournamentsResponse.ok) { setTournaments([]); return; }

        const allTournaments = await tournamentsResponse.json();
        if (!Array.isArray(allTournaments) || allTournaments.length === 0) { setTournaments([]); return; }

        const userTournaments = [];

        for (const tournament of allTournaments) {
          const tournamentId = tournament.id || tournament.tournamentId || tournament.TournamentId;
          if (!tournamentId) continue;

          try {
            const teamsResponse = await fetch(
              `https://api.asterickx.com/api/Teams/by-tournament/${tournamentId}`,
              { headers }
            );
            if (!teamsResponse.ok) continue;

            const tournamentTeams = await teamsResponse.json();
            if (!Array.isArray(tournamentTeams) || tournamentTeams.length === 0) continue;

            for (const team of tournamentTeams) {
              const teamId = team.id || team.teamId || team.TeamId;
              if (!teamId) continue;

              try {
                const membersResponse = await fetch(
                  `https://api.asterickx.com/api/Teams/${teamId}/members`,
                  { headers }
                );
                if (!membersResponse.ok) continue;

                const teamMembers = await membersResponse.json();
                if (!Array.isArray(teamMembers)) continue;

                const isUserInTeam = teamMembers.some(member => {
                  const memberId = String(member.id || member.memberId || member.userId || member.playerId || '');
                  return memberId === String(userId);
                });

                if (isUserInTeam) {
                  userTournaments.push({
                    id: `${tournamentId}-${teamId}`,
                    tournamentId,
                    tournamentName: tournament.name || tournament.tournamentName || `Tournament ${tournamentId}`,
                    startDate: tournament.startDate || tournament.StartDate,
                    endDate: tournament.endDate || tournament.EndDate,
                    location: tournament.location || tournament.venue || tournament.Location || tournament.Venue,
                    teamId,
                    teamName: team.name || team.teamName || `Team ${teamId}`,
                    teamCode: team.code || team.teamCode || team.Code || team.TeamCode,
                  });
                  break; // user found in this tournament, move to next
                }
              } catch (memberErr) {
                console.error(`Error fetching members for team ${teamId}:`, memberErr);
              }
            }
          } catch (teamErr) {
            console.error(`Error fetching teams for tournament ${tournamentId}:`, teamErr);
          }
        }

        setTournaments(userTournaments);
      } catch (err) {
        console.error('Tournament/Team fetch error:', err);
        setTournaments([]);
      }

    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ─── Loading / Error states ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error Loading Profile</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  const displayUserData = userDetails || userData;
  const userPhotoUrl = getUserPhotoUrl(displayUserData);
  const userPhone = getUserPhone(displayUserData);
  const userState = getUserState(displayUserData);

  return (
    <div className="container mt-5" style={{ marginBottom: '80px' }}>
      <div className="row">

        {/* ── ID CARD (Adminprofile styling) ── */}
        <div className="col-md-3 mb-4">
          <div
            ref={idCardRef}
            className="card shadow h-100"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '12px',
            }}
          >
            {/* Dark overlay */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0, 0, 0, 0.50)', zIndex: 0,
            }} />

            {/* Logo section */}
            <div
              data-logo-section="true"
              style={{
                position: 'relative', zIndex: 1,
                // background: 'rgba(255,255,255,0.13)',
                // borderBottom: '1px solid rgba(255,255,255,0.18)',
                padding: '12px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              }}
            >
              <img src={logoImage} alt="Logo" style={{ height: '50px', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div className="card-body text-center" style={{ position: 'relative', zIndex: 1, color: '#f0f0f0' }}>

              {/* Profile photo */}
              <div className="mb-3">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center overflow-hidden"
                  style={{
                    width: '120px', height: '120px',
                    border: '4px solid rgba(255,255,255,0.75)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                    position: 'relative',
                    background: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                  }}
                >
                  {userPhotoUrl ? (
                    <>
                      <img
                        src={userPhotoUrl}
                        alt="Profile"
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          position: 'absolute', top: 0, left: 0, display: 'block', opacity: 0.9,
                        }}
                        onLoad={e => { e.target.style.opacity = '0.9'; }}
                        onError={e => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.fa-user')) {
                            const icon = document.createElement('i');
                            icon.className = 'fas fa-user fa-3x text-white';
                            icon.style.position = 'relative';
                            icon.style.zIndex = '1';
                            parent.appendChild(icon);
                          }
                        }}
                      />
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        boxShadow: 'inset 0 0 12px rgba(0,0,0,0.25)',
                        zIndex: 1, pointerEvents: 'none',
                      }} />
                    </>
                  ) : (
                    <i className="fas fa-user fa-2x" style={{ color: 'rgba(255,255,255,0.7)' }}></i>
                  )}
                </div>
              </div>

              {/* Name & info */}
              <div className="mb-4">
                <h5 className="mb-1" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)', fontWeight: 700 }}>
                  {displayUserData?.firstName && displayUserData?.lastName
                    ? `${displayUserData.firstName} ${displayUserData.lastName}`
                    : displayUserData?.name || 'N/A'}
                </h5>
                {userPhone && (
                  <p className="mb-1">
                    <i className="fas fa-phone me-2"></i>
                    <small className="fw-semibold">{userPhone}</small>
                  </p>
                )}
                {userState && (
                  <p className="mb-1">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    <small className="fw-semibold">{userState}</small>
                  </p>
                )}
              </div>

              {/* QR Code */}
              <div className="mb-4">
                <div className="bg-white p-3 rounded d-inline-block" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  {qrData ? (
                    <QRCode value={qrData} size={120} level="M" />
                  ) : (
                    <div style={{ width: '120px', height: '120px' }} className="d-flex align-items-center justify-content-center">
                      <div className="spinner-border text-primary"></div>
                    </div>
                  )}
                </div>
                <p className="mt-2 mb-0"><small className="opacity-75">Scan for details</small></p>
              </div>

              {/* Download button */}
              <div className="gap-2" data-exclude-from-capture="true">
                <button className="btn btn-primary fw-bold" onClick={downloadIDCard} disabled={downloading}>
                  {downloading ? (
                    <><div className="spinner-border spinner-border-sm me-2"></div>Downloading...</>
                  ) : (
                    <><i className="fas fa-download me-2"></i>Download ID Card</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── TOURNAMENT REGISTRATIONS (from Profile) ── */}
        <div className="col-md-9 mb-4">
          <div className="card shadow">
            <div className="card-header text-white" style={{ backgroundColor: '#3069B5' }}>
              <h4 className="mb-0"><i className="fas fa-trophy me-2"></i>Tournament Registrations</h4>
            </div>
            <div className="card-body">
              {tournaments.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-info-circle fa-3x mb-3"></i>
                  <h5>No tournaments registered yet</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Tournament Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Team Name</th>
                        <th className="text-center">Dashboard</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tournaments.map((t, i) => (
                        <tr key={t.id || i}>
                          <td>{i + 1}</td>
                          <td>
                            <strong>{t.tournamentName}</strong>
                            {t.location && (
                              <small className="d-block text-muted">
                                <i className="fas fa-map-marker-alt me-1"></i>{t.location}
                              </small>
                            )}
                          </td>
                          <td><span className="badge bg-info">{formatDate(t.startDate)}</span></td>
                          <td><span className="badge bg-info">{formatDate(t.endDate)}</span></td>
                          <td>
                            <span className="fw-bold text-primary">{t.teamName}</span>
                            {t.teamCode && (
                              <small className="d-block text-muted">Code: {t.teamCode}</small>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-primary dashboard-btn"
                              onClick={() => navigate(`/tournamentdashboard`)}
                              title="View Tournament Dashboard"
                            >
                              <i className="fas fa-chart-line me-1"></i>View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Profile;